import { getCurrentInstance, ref } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { matchObjsShape } from '@agritask/js-utils/libs/object_utils';
import { AtToastTypes } from '@agritask/vue-components';
import { createToast } from '@agritask/vue-components/utils';
import { axiosGet } from '../utils/api';
import { redirectToHomePage } from '../utils/redirectTo';
import { LoginPageNames } from './routes';
const LOGIN_API_PREFIX = 'api/public/login/v1';
const languagesListShape = [
    {
        code: '',
        title: '',
    },
];
const redirectLink = ref();
export default function useLoginApi(baseUrl) {
    const isLoading = ref(false);
    const resetByUsername = ref(false);
    const vm = getCurrentInstance();
    const { t } = useI18n();
    const router = useRouter();
    function performLogin(payload) {
        isLoading.value = true;
        axios
            .post(`${baseUrl}${LOGIN_API_PREFIX}/login`, payload)
            .then((response) => {
            redirectLink.value = response.headers.location;
            if (!redirectLink.value) {
                console.error('Redirect Link (Location header) for Login was not returned from the BE');
                return;
            }
            if (response.data.showUserAgreement) {
                router.push({ name: LoginPageNames.Consent });
            }
            else {
                location.replace(redirectLink.value);
            }
        })
            .catch((e) => {
            if (e.response?.data?.message_key) {
                createToast(vm?.proxy, {
                    title: t(`login.${e.response.data.message_key}`),
                    type: AtToastTypes.AtToastType.Error,
                });
                return;
            }
            if (e.response.status === 401) {
                createToast(vm?.proxy, {
                    title: t('login.error.invalid_credentials_401'),
                    type: AtToastTypes.AtToastType.Error,
                });
            }
            else if (e.response.status === 406) {
                createToast(vm?.proxy, {
                    title: t('login.error.login_406_error'),
                    type: AtToastTypes.AtToastType.Error,
                });
            }
            else {
                createToast(vm?.proxy, {
                    title: t('login.error.login_500_title'),
                    message: t('login.error.login_500'),
                    type: AtToastTypes.AtToastType.Error,
                });
            }
        })
            .finally(() => (isLoading.value = false));
    }
    const confirmGDPR = () => {
        return axios
            .post(`${baseUrl}rest/consent/accept`)
            .then(() => {
            redirectLink.value ? location.replace(redirectLink.value) : redirectToHomePage();
        })
            .catch((e) => {
            if (e.response?.data?.message_key) {
                createToast(vm?.proxy, {
                    title: t(`login.${e.response.data.message_key}`),
                    type: AtToastTypes.AtToastType.Error,
                });
                return;
            }
            e.response.status === 401
                ? createToast(vm?.proxy, {
                    title: t('login.error.invalid_credentials_401'),
                    type: AtToastTypes.AtToastType.Error,
                })
                : createToast(vm?.proxy, {
                    title: t('login.error.login_500_title'),
                    message: t('login.error.login_500'),
                    type: AtToastTypes.AtToastType.Error,
                });
        });
    };
    async function getLanguagesList() {
        const response = await axiosGet(`${baseUrl}${LOGIN_API_PREFIX}/languages`);
        return matchObjsShape(languagesListShape, response);
    }
    function resetPassword(payload) {
        isLoading.value = true;
        axios
            .post(`${baseUrl}${LOGIN_API_PREFIX}/reset`, payload)
            .then((response) => {
            if (response.data.type === 'INFO') {
                resetByUsername.value = true;
            }
            else {
                const toastType = response.data.message_key.includes('success')
                    ? AtToastTypes.AtToastType.Success
                    : AtToastTypes.AtToastType.Error;
                createToast(vm?.proxy, {
                    title: t(`login.reset_password.${response.data.message_key}`),
                    type: toastType,
                });
            }
        })
            .catch((e) => {
            createToast(vm?.proxy, {
                title: t(`login.reset_password.${e.response?.data.message_key || 'others_error'}`),
                type: AtToastTypes.AtToastType.Error,
            });
        })
            .finally(() => (isLoading.value = false));
    }
    async function loginOrganizationWithEmail(email) {
        isLoading.value = true;
        axios
            .get(`${baseUrl}api/public/login/sso/v1/organization`, {
            params: { email },
        })
            .then((response) => window.location.assign(response.headers.location))
            .catch((e) => {
            const errorMessage = e.response?.data.message_key;
            const toastOptions = {
                title: errorMessage || t('errors.default.title'),
                type: AtToastTypes.AtToastType.Error,
            };
            if (!errorMessage) {
                toastOptions.message = t('errors.default.description');
            }
            createToast(vm?.proxy, toastOptions);
        })
            .finally(() => (isLoading.value = false));
    }
    return {
        performLogin,
        isLoading,
        getLanguagesList,
        resetPassword,
        resetByUsername,
        confirmGDPR,
        redirectLink,
        loginOrganizationWithEmail,
    };
}
